import React, { useCallback, useEffect, useState } from 'react';
import { confirmWrapper } from 'src/components/Dialog';
import { Currency } from 'src/hooks/query/currencies/types';
import API from '../../network';
import ViewRenderer from './views';
import { AddCurrency } from './components';
import { CurrencyFormSubmitHandler, CurrenciesViewRendererProps } from './types';

function Currencies() {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [filterCurrencies, setFilterCurrencies] = useState<Currency[]>([]);
  const [currenciesFetching, setCurrenciesFetching] = useState(true);

  const getAllCurrencies = useCallback(async () => {
    setCurrenciesFetching(true);
    setCurrencies([]);
    setFilterCurrencies([]);
    try {
      const response: Currency[] = await API.getInstance().fetchAllCurrencies();
      setCurrencies(response);
      setFilterCurrencies(response);
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      setCurrenciesFetching(false);
    }
  }, []);

  useEffect(() => {
    getAllCurrencies();
  }, [getAllCurrencies]);

  const onDeleteRow = async (data: Currency) => {
    const { code, id = 0 } = data;
    const deleteConfirmed = await confirmWrapper(`Are you sure want to deactivate ${code}?`, {
      variant: 'danger',
    });
    if (deleteConfirmed) {
      try {
        await API.getInstance().deleteCurrency(id);
        await confirmWrapper('Currency deactivated successfully!', {
          hideSecondaryBtn: true,
          variant: 'success',
          autoClose: true,
        });
        getAllCurrencies();
      } catch (e) {
        await confirmWrapper('Something went wrong', {
          hideSecondaryBtn: true,
          variant: 'danger',
        });
      }
    }
  };

  const createCurrency = async (data: CurrencyFormSubmitHandler): Promise<void> => {
    const { values } = data;
    try {
      await API.getInstance().createCurrency({
        code: values.code,
        symbol: values.symbol,
        name: values.name,
        sortOrder: values.sortOrder,
      });
      getAllCurrencies();
    } catch (e: any) {
      await confirmWrapper(e?.response?.data?.message || 'Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
      throw e;
    }
  };

  const updateCurrency = async (data: CurrencyFormSubmitHandler): Promise<void> => {
    const { values } = data;
    const {
      id,
      code,
      symbol,
      name,
      sortOrder,
      status,
    } = values;
    try {
      await API.getInstance().updateCurrency({
        id,
        code,
        symbol,
        name,
        sortOrder,
        status,
      });
      getAllCurrencies();
    } catch (e: any) {
      await confirmWrapper(e?.response?.data?.message || 'Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
      throw e;
    }
  };

  const showCreateCurrency = async () => {
    await confirmWrapper('', {
      variant: '',
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: 'dialogSmall',
      fullWidth: true,
      containerClasses: 'p-0',
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: <AddCurrency onCreateCurrency={createCurrency} />,
    });
  };

  const onEditRow = async (data: Currency) => {
    await confirmWrapper('', {
      variant: '',
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: 'dialogSmall',
      fullWidth: true,
      showCloseButton: true,
      containerClasses: 'p-0',
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <AddCurrency
          defaultValues={{
            id: data.id,
            code: data.code,
            symbol: data.symbol,
            name: data.name,
            sortOrder: data.sortOrder,
            status: data.status,
          }}
          onCreateCurrency={updateCurrency}
        />
      ),
    });
  };

  const onChange = (searchString: string) => {
    const filteredObjects = currencies.filter(({ code, name, symbol }) => {
      const query = searchString.toLowerCase();
      return (code && code.toLowerCase().includes(query))
        || (name && name.toLowerCase().includes(query))
        || (symbol && symbol.toLowerCase().includes(query));
    });
    setFilterCurrencies(filteredObjects);
  };

  const viewProps: CurrenciesViewRendererProps = {
    currencies,
    filterCurrencies,
    currenciesFetching,
    showCreateCurrency: () => {
      showCreateCurrency().catch(() => {});
    },
    onDeleteRow: (data) => {
      onDeleteRow(data).catch(() => {});
    },
    onEditRow: (data) => {
      onEditRow(data).catch(() => {});
    },
    onChange,
  };

  return <ViewRenderer {...viewProps} />;
}

export default Currencies;
